import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Users, Shield, CheckCircle, Clock, Coins,
  Briefcase, BookOpen, Building2, GraduationCap, Plus,
  BarChart3, RefreshCw, Bell, FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import apiClient from '../../services/apiClient';

// Shared components
import { StatsCard, getApiErrorMessage } from './components/AdminComponents';

// Tab components
import OverviewTab from './tabs/OverviewTab';
import ReviewsTab from './tabs/ReviewsTab';
import ApprovedTab from './tabs/ApprovedTab';
import ContentTab from './tabs/ContentTab';
import NotificationsTab from './tabs/NotificationsTab';
import UsersTab from './tabs/UsersTab';
import CreatePostTab from './tabs/CreatePostTab';

// Modal components
import {
  ReviewModal, PriceModal, WalletModal,
  DeleteModal, EditModal, EditAdminPostModal
} from './components/AdminModals';

export const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Core state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data state
  const [analytics, setAnalytics] = useState(null);
  const [growthData, setGrowthData] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [allContent, setAllContent] = useState({ jobs: [], tuitions: [], tutors: [], organizations: [] });
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [adminPosts, setAdminPosts] = useState({ jobs: [], tuitions: [] });

  // Filter state
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [contentTypeFilter, setContentTypeFilter] = useState('all');
  const [contentStatusFilter, setContentStatusFilter] = useState('all');
  const [contentSearch, setContentSearch] = useState('');
  const [contentSortBy, setContentSortBy] = useState('date_desc');
  const [dateRangeFilter, setDateRangeFilter] = useState({ start: '', end: '' });
  const [priceRangeFilter, setPriceRangeFilter] = useState({ min: '', max: '' });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Admin post management
  const [adminPostSearch, setAdminPostSearch] = useState('');
  const [adminPostFilter, setAdminPostFilter] = useState('all');
  const [editingAdminPost, setEditingAdminPost] = useState(null);

  // Create post form state
  const [jobForm, setJobForm] = useState({
    title: '', organization: '', location: '', salary: '', job_type: 'full_time',
    subjects: '', experience: '', description: '',
    contact_name: '', contact_email: '', contact_phone: '', unlock_price: 10,
    latitude: '', longitude: ''
  });
  const [tuitionForm, setTuitionForm] = useState({
    category: '', description: '', board: 'cbse', city: '', state: '',
    class_mode: 'offline_student_place', budget_type: 'monthly', budget_amount: 5000,
    subjects: '', contact_name: '', contact_phone: '', unlock_price: 10,
    latitude: '', longitude: ''
  });

  // Modal state
  const [reviewModal, setReviewModal] = useState({ open: false, item: null, type: '' });
  const [priceModal, setPriceModal] = useState({ open: false, item: null, price: 0 });
  const [walletModal, setWalletModal] = useState({ open: false, user: null, amount: 0, description: '' });
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
  const [editModal, setEditModal] = useState({ open: false, item: null, type: '', data: {} });

  // ─── Effects ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchAllData();
  }, [user, navigate]);

  // ─── Data Fetching ─────────────────────────────────────────────────────
  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchAnalytics(),
        fetchGrowthData(),
        fetchPendingItems(),
        fetchUsers(),
        fetchAllContent(),
        fetchAdminPosts(),
        fetchNotifications()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  const fetchAdminPosts = async () => {
    try {
      const data = await apiClient.get('/admin/posts/my');
      setAdminPosts({
        jobs: data.jobs || [],
        tuitions: data.tuition_posts || []
      });
    } catch (error) {
      console.error('Error fetching admin posts:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const data = await apiClient.get('/admin/analytics/overview');
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchGrowthData = async () => {
    try {
      const data = await apiClient.get('/admin/analytics/users-growth?days=14');
      setGrowthData(data.growth_data || []);
    } catch (error) {
      console.error('Error fetching growth data:', error);
    }
  };

  const fetchPendingItems = async () => {
    try {
      const [jobsData, tuitionsData, tutorsData, orgsData] = await Promise.all([
        apiClient.get('/admin/jobs'),
        apiClient.get('/admin/tuition-posts'),
        apiClient.get('/admin/tutors'),
        apiClient.get('/admin/organizations')
      ]);

      const pending = [
        ...(jobsData || []).filter(j => j && j.review_status === 'pending').map(j => ({ ...j, type: 'job' })),
        ...(tuitionsData.posts || []).filter(t => t && t.review_status === 'pending').map(t => ({ ...t, type: 'tuition' })),
        ...(tutorsData || []).filter(t => t && t.review_status === 'pending').map(t => ({ ...t, type: 'tutor' })),
        ...(orgsData || []).filter(o => o && o.review_status === 'pending').map(o => ({ ...o, type: 'organization' }))
      ].filter(item => item && item.id);

      setPendingItems(pending);
    } catch (error) {
      console.error('Error fetching pending items:', error);
      setPendingItems([]);
    }
  };

  const fetchAllContent = async () => {
    try {
      const [jobsData, tuitionsData, tutorsData, orgsData] = await Promise.all([
        apiClient.get('/admin/jobs'),
        apiClient.get('/admin/tuition-posts'),
        apiClient.get('/admin/tutors'),
        apiClient.get('/admin/organizations')
      ]);

      setAllContent({
        jobs: (Array.isArray(jobsData) ? jobsData : []).map(j => ({ ...j, type: 'job' })),
        tuitions: (tuitionsData?.posts || []).map(t => ({ ...t, type: 'tuition' })),
        tutors: (Array.isArray(tutorsData) ? tutorsData : []).map(t => ({ ...t, type: 'tutor' })),
        organizations: (Array.isArray(orgsData) ? orgsData : []).map(o => ({ ...o, type: 'organization' }))
      });
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await apiClient.get('/admin/users?limit=100');
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const data = await apiClient.get('/admin/notifications?limit=50');
      setNotifications(data.notifications || []);
      setUnreadNotifications(data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  // ─── Computed (Memos) ──────────────────────────────────────────────────
  const filteredAdminJobs = adminPosts.jobs.filter(job => {
    if (adminPostFilter !== 'all' && adminPostFilter !== 'jobs') return false;
    if (adminPostSearch && !job.title?.toLowerCase().includes(adminPostSearch.toLowerCase()) && !job.organization?.toLowerCase().includes(adminPostSearch.toLowerCase())) return false;
    return true;
  });

  const filteredAdminTuitions = adminPosts.tuitions.filter(post => {
    if (adminPostFilter !== 'all' && adminPostFilter !== 'tuitions') return false;
    if (adminPostSearch && !post.category?.toLowerCase().includes(adminPostSearch.toLowerCase()) && !post.city?.toLowerCase().includes(adminPostSearch.toLowerCase())) return false;
    return true;
  });

  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const matchesSearch = !userSearch ||
        u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.phone?.includes(userSearch);
      const matchesRole = userRoleFilter === 'all' || u.role === userRoleFilter;
      const matchesStatus = userStatusFilter === 'all' ||
        (userStatusFilter === 'active' && u.is_active) ||
        (userStatusFilter === 'suspended' && !u.is_active);
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, userSearch, userRoleFilter, userStatusFilter]);

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(start, start + itemsPerPage);
  }, [filteredUsers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const filteredContent = useMemo(() => {
    let items = [];

    if (contentTypeFilter === 'all' || contentTypeFilter === 'job') {
      items = [...items, ...allContent.jobs];
    }
    if (contentTypeFilter === 'all' || contentTypeFilter === 'tuition') {
      items = [...items, ...allContent.tuitions];
    }
    if (contentTypeFilter === 'all' || contentTypeFilter === 'tutor') {
      items = [...items, ...allContent.tutors];
    }
    if (contentTypeFilter === 'all' || contentTypeFilter === 'organization') {
      items = [...items, ...allContent.organizations];
    }

    if (contentStatusFilter !== 'all') {
      items = items.filter(item => item.review_status === contentStatusFilter);
    }

    if (contentSearch.trim()) {
      const searchLower = contentSearch.toLowerCase();
      items = items.filter(item => {
        const title = getItemTitle(item).toLowerCase();
        const description = (item.description || item.bio || '').toLowerCase();
        const subjects = Array.isArray(item.subjects) ? item.subjects.join(' ').toLowerCase() : '';
        const city = (item.city || '').toLowerCase();
        return title.includes(searchLower) ||
          description.includes(searchLower) ||
          subjects.includes(searchLower) ||
          city.includes(searchLower);
      });
    }

    if (dateRangeFilter.start || dateRangeFilter.end) {
      items = items.filter(item => {
        const itemDate = new Date(item.created_at || item.updated_at);
        const startMatch = !dateRangeFilter.start || itemDate >= new Date(dateRangeFilter.start);
        const endMatch = !dateRangeFilter.end || itemDate <= new Date(dateRangeFilter.end);
        return startMatch && endMatch;
      });
    }

    if (priceRangeFilter.min || priceRangeFilter.max) {
      items = items.filter(item => {
        const price = item.unlock_price || 0;
        const minMatch = !priceRangeFilter.min || price >= parseInt(priceRangeFilter.min);
        const maxMatch = !priceRangeFilter.max || price <= parseInt(priceRangeFilter.max);
        return minMatch && maxMatch;
      });
    }

    items.sort((a, b) => {
      switch (contentSortBy) {
        case 'date_desc':
          return new Date(b.created_at || b.updated_at) - new Date(a.created_at || a.updated_at);
        case 'date_asc':
          return new Date(a.created_at || a.updated_at) - new Date(b.created_at || b.updated_at);
        case 'price_desc':
          return (b.unlock_price || 0) - (a.unlock_price || 0);
        case 'price_asc':
          return (a.unlock_price || 0) - (b.unlock_price || 0);
        case 'views_desc':
          return (b.view_count || 0) - (a.view_count || 0);
        case 'name_asc':
          return getItemTitle(a).localeCompare(getItemTitle(b));
        default:
          return 0;
      }
    });

    return items.filter(item => item && item.id);
  }, [allContent, contentTypeFilter, contentStatusFilter, contentSearch, dateRangeFilter, priceRangeFilter, contentSortBy]);

  // ─── Helpers ───────────────────────────────────────────────────────────
  const getItemIcon = (type) => {
    const iconClass = "h-5 w-5";
    switch (type) {
      case 'job': return <Briefcase className={`${iconClass} text-blue-600`} />;
      case 'tuition': return <BookOpen className={`${iconClass} text-green-600`} />;
      case 'tutor': return <GraduationCap className={`${iconClass} text-purple-600`} />;
      case 'organization': return <Building2 className={`${iconClass} text-orange-600`} />;
      default: return null;
    }
  };

  const getItemTitle = (item) => {
    if (!item) return 'Unknown';
    if (item.type === 'job') return item.title || 'Untitled Job';
    if (item.type === 'tuition') return item.category || 'Untitled Tuition';
    if (item.type === 'tutor') return item.full_name || item.name || 'Unknown Tutor';
    if (item.type === 'organization') return item.organization_name || item.name || 'Unknown Organization';
    return 'Unknown';
  };

  // ─── Action Handlers ──────────────────────────────────────────────────
  const handleReview = (item, type) => {
    setReviewModal({ open: true, item, type });
  };

  const handleSetPrice = (item) => {
    setPriceModal({ open: true, item, price: item.unlock_price || 0 });
  };

  const handleAddCoins = (targetUser) => {
    setWalletModal({ open: true, user: targetUser, amount: 0, description: 'Admin credit' });
  };

  const handleSuspendUser = async (targetUser) => {
    try {
      await apiClient.put(
        `/admin/users/${targetUser.id}/status`,
        { is_active: !targetUser.is_active }
      );
      toast.success(`User ${targetUser.is_active ? 'suspended' : 'activated'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = (targetUser) => {
    setDeleteModal({ open: true, user: targetUser });
  };

  const handleToggleVerification = async (targetUser) => {
    try {
      await apiClient.put(
        `/admin/users/${targetUser.id}/verification`,
        { is_verified: !targetUser.is_verified }
      );
      toast.success(`User ${!targetUser.is_verified ? 'verified' : 'unverified'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update verification status');
    }
  };

  const handleEdit = (item, type) => {
    setEditModal({ open: true, item, type, data: { ...item } });
  };

  const confirmDeleteUser = async () => {
    try {
      await apiClient.delete(`/admin/users/${deleteModal.user.id}`);
      toast.success('User deleted successfully');
      setDeleteModal({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const approveItem = async () => {
    const { item, type } = reviewModal;
    try {
      const endpoint = {
        job: `/admin/jobs/${item.id}`,
        tuition: `/admin/tuition-posts/${item.id}`,
        tutor: `/admin/tutors/${item.id}`,
        organization: `/admin/organizations/${item.id}`
      }[type];

      await apiClient.put(endpoint, { review_status: 'approved' });

      toast.success(`${type} approved successfully!`);
      setReviewModal({ open: false, item: null, type: '' });
      fetchPendingItems();
      fetchAllContent();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to approve'));
    }
  };

  const rejectItem = async () => {
    const { item, type } = reviewModal;
    try {
      const endpoint = {
        job: `/admin/jobs/${item.id}`,
        tuition: `/admin/tuition-posts/${item.id}`,
        tutor: `/admin/tutors/${item.id}`,
        organization: `/admin/organizations/${item.id}`
      }[type];

      await apiClient.put(endpoint, { review_status: 'rejected' });

      toast.success(`${type} rejected`);
      setReviewModal({ open: false, item: null, type: '' });
      fetchPendingItems();
      fetchAllContent();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to reject'));
    }
  };

  const savePrice = async () => {
    const { item, price } = priceModal;
    try {
      const endpoint = {
        job: `/admin/jobs/${item.id}`,
        tuition: `/admin/tuition-posts/${item.id}`,
        tutor: `/admin/tutors/${item.id}`,
        organization: `/admin/organizations/${item.id}`
      }[item.type];

      await apiClient.put(endpoint, { unlock_price: parseInt(price) });

      toast.success('Price updated successfully!');
      setPriceModal({ open: false, item: null, price: 0 });
      fetchPendingItems();
      fetchAllContent();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update price'));
    }
  };

  const saveEdit = async () => {
    const { item, type, data } = editModal;
    try {
      const endpoint = {
        job: `/admin/jobs/${item.id}/edit`,
        tuition: `/admin/tuition-posts/${item.id}/edit`,
        tutor: `/admin/tutors/${item.id}/edit`,
        organization: `/admin/organizations/${item.id}/edit`,
        user: `/admin/users/${item.id}/edit`
      }[type];

      const editableFieldsByType = {
        job: ['title', 'type', 'location', 'salary', 'subjects', 'experience', 'description', 'organization', 'latitude', 'longitude', 'review_status', 'unlock_price'],
        tuition: ['category', 'description', 'subjects', 'board', 'class_mode', 'start_time', 'availability', 'gender_preference', 'budget_type', 'budget', 'budget_amount', 'address', 'city', 'state', 'latitude', 'longitude', 'admin_provided_contact', 'review_status', 'unlock_price'],
        tutor: ['full_name', 'username', 'dob', 'gender', 'qualification', 'experience_years', 'subjects', 'teaching_mode', 'teaching_category', 'hourly_rate', 'monthly_rate', 'salary_type', 'address', 'city', 'state', 'pincode', 'latitude', 'longitude', 'bio', 'photo_url', 'profile_photo', 'spoken_languages', 'educational_history', 'achievements', 'awards', 'intro_video_url', 'review_status', 'unlock_price'],
        organization: ['organization_name', 'organization_type', 'registration_number', 'contact_person_name', 'contact_email', 'contact_phone', 'address', 'city', 'state', 'pincode', 'latitude', 'longitude', 'description', 'about', 'services_offered', 'services', 'logo_url', 'logo', 'review_status', 'unlock_price'],
        user: ['email', 'phone', 'is_active', 'is_verified', 'wallet_balance', 'role']
      };

      const allowedFields = editableFieldsByType[type] || [];
      const cleanData = {};
      for (const [key, value] of Object.entries(data)) {
        if (allowedFields.includes(key) && value !== undefined && value !== null && value !== '') {
          if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) continue;
          if (Array.isArray(value) && value.length === 0) continue;
          cleanData[key] = value;
        }
      }

      if (type === 'tuition') {
        if (Array.isArray(cleanData.availability)) {
          cleanData.availability = cleanData.availability.join(', ');
        }
        if (cleanData.budget) cleanData.budget = parseInt(cleanData.budget) || 0;
        if (cleanData.budget_amount) cleanData.budget_amount = parseInt(cleanData.budget_amount) || 0;
        if (cleanData.unlock_price) cleanData.unlock_price = parseInt(cleanData.unlock_price) || 0;
        if (cleanData.latitude) cleanData.latitude = parseFloat(cleanData.latitude) || null;
        if (cleanData.longitude) cleanData.longitude = parseFloat(cleanData.longitude) || null;
        if (cleanData.latitude === null) delete cleanData.latitude;
        if (cleanData.longitude === null) delete cleanData.longitude;
      }

      if (type === 'tutor') {
        if (cleanData.experience_years) cleanData.experience_years = parseInt(cleanData.experience_years) || 0;
        if (cleanData.hourly_rate) cleanData.hourly_rate = parseInt(cleanData.hourly_rate) || 0;
        if (cleanData.monthly_rate) cleanData.monthly_rate = parseInt(cleanData.monthly_rate) || 0;
        if (cleanData.unlock_price) cleanData.unlock_price = parseInt(cleanData.unlock_price) || 0;
        if (cleanData.latitude) cleanData.latitude = parseFloat(cleanData.latitude) || null;
        if (cleanData.longitude) cleanData.longitude = parseFloat(cleanData.longitude) || null;
        if (cleanData.latitude === null) delete cleanData.latitude;
        if (cleanData.longitude === null) delete cleanData.longitude;
      }

      console.log('saveEdit sending:', type, cleanData);

      await apiClient.patch(endpoint, cleanData);

      toast.success(`${type} updated successfully!`);
      setEditModal({ open: false, item: null, type: '', data: {} });
      fetchPendingItems();
      fetchAllContent();
      if (type === 'user') {
        fetchUsers();
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update'));
      console.error('Edit error:', error);
    }
  };

  const addWalletCoins = async () => {
    const { user: targetUser, amount, description } = walletModal;
    try {
      await apiClient.post(
        '/admin/wallet/credit',
        { user_id: targetUser.id, amount: parseInt(amount), description: description || 'Admin credit' }
      );

      toast.success(`${amount} coins added to ${targetUser.email}'s wallet!`);
      setWalletModal({ open: false, user: null, amount: 0, description: '' });
      fetchUsers();
    } catch (error) {
      toast.error('Failed to add coins');
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...jobForm,
        subjects: jobForm.subjects.split(',').map(s => s.trim()).filter(Boolean),
        latitude: jobForm.latitude ? parseFloat(jobForm.latitude) : null,
        longitude: jobForm.longitude ? parseFloat(jobForm.longitude) : null
      };
      await apiClient.post('/admin/jobs/create', payload);
      toast.success('Job created successfully!');
      setJobForm({
        title: '', organization: '', location: '', salary: '', job_type: 'full_time',
        subjects: '', experience: '', description: '',
        contact_name: '', contact_email: '', contact_phone: '', unlock_price: 10,
        latitude: '', longitude: ''
      });
      fetchAdminPosts();
      fetchAllContent();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create job'));
    }
  };

  const handleCreateTuition = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...tuitionForm,
        subjects: tuitionForm.subjects.split(',').map(s => s.trim()).filter(Boolean),
        availability: ['any'],
        latitude: tuitionForm.latitude ? parseFloat(tuitionForm.latitude) : null,
        longitude: tuitionForm.longitude ? parseFloat(tuitionForm.longitude) : null
      };
      await apiClient.post('/admin/tuition-posts/create', payload);
      toast.success('Tuition post created successfully!');
      setTuitionForm({
        category: '', description: '', board: 'cbse', city: '', state: '',
        class_mode: 'offline_student_place', budget_type: 'monthly', budget_amount: 5000,
        subjects: '', contact_name: '', contact_phone: '', unlock_price: 10,
        latitude: '', longitude: ''
      });
      fetchAdminPosts();
      fetchAllContent();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create tuition post'));
    }
  };

  const handleEditAdminPost = async () => {
    if (!editingAdminPost) return;
    try {
      const { type, id, ...rawData } = editingAdminPost;
      const endpoint = type === 'job' ? `/admin/jobs/${id}/edit` : `/admin/tuition-posts/${id}/edit`;

      const editableFieldsByType = {
        job: ['title', 'location', 'salary', 'subjects', 'experience', 'description', 'organization', 'job_type', 'latitude', 'longitude', 'review_status', 'unlock_price', 'admin_provided_contact'],
        tuition: ['category', 'description', 'subjects', 'board', 'class_mode', 'start_time', 'availability', 'gender_preference', 'budget_type', 'budget', 'budget_amount', 'address', 'city', 'state', 'latitude', 'longitude', 'admin_provided_contact', 'review_status', 'unlock_price']
      };

      if (rawData.contact_name || rawData.contact_phone || rawData.contact_email) {
        rawData.admin_provided_contact = {
          ...(rawData.admin_provided_contact || {}),
          name: rawData.contact_name || rawData.admin_provided_contact?.name || '',
          phone: rawData.contact_phone || rawData.admin_provided_contact?.phone || '',
          email: rawData.contact_email || rawData.admin_provided_contact?.email || ''
        };
      }

      const allowedFields = editableFieldsByType[type] || [];
      const cleanData = {};
      for (const [key, value] of Object.entries(rawData)) {
        if (allowedFields.includes(key) && value !== undefined && value !== null && value !== '') {
          cleanData[key] = value;
        }
      }

      await apiClient.patch(endpoint, cleanData);
      toast.success(`${type === 'job' ? 'Job' : 'Tuition post'} updated successfully!`);
      setEditingAdminPost(null);
      fetchAdminPosts();
      fetchAllContent();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update'));
    }
  };

  // ─── Loading Screen ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin h-16 w-16 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            <Shield className="h-8 w-8 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-gray-600 mt-4 font-medium">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Top Header Bar */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="bg-gradient-to-r from-red-600 to-red-700 p-2 rounded-xl shadow-lg">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-xs text-gray-500">Avyudha Platform Management</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <div className="relative">
                <Button variant="outline" size="sm">
                  <Bell className="h-4 w-4" />
                </Button>
                {pendingItems.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                    {pendingItems.length}
                  </span>
                )}
              </div>
              <Button onClick={logout} variant="outline" size="sm">Logout</Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <StatsCard
            title="Total Users"
            value={analytics?.users?.total || 0}
            change={12}
            changeType="increase"
            icon={Users}
            color="bg-blue-500"
          />
          <StatsCard
            title="Students"
            value={analytics?.users?.students || 0}
            icon={GraduationCap}
            color="bg-green-500"
          />
          <StatsCard
            title="Tutors"
            value={analytics?.users?.tutors || 0}
            subtext={`${analytics?.profiles?.verified_tutors || 0} verified`}
            icon={Users}
            color="bg-purple-500"
          />
          <StatsCard
            title="Organizations"
            value={analytics?.users?.organizations || 0}
            icon={Building2}
            color="bg-orange-500"
          />
          <StatsCard
            title="Pending Reviews"
            value={pendingItems.length}
            icon={Clock}
            color="bg-yellow-500"
          />
          <StatsCard
            title="Active Posts"
            value={analytics?.content?.tuition_posts?.active || 0}
            icon={FileText}
            color="bg-indigo-500"
          />
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl shadow-sm border">
            <TabsTrigger value="overview" className="rounded-lg px-6">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-lg px-6">
              <Clock className="h-4 w-4 mr-2" />
              Reviews
              {pendingItems.length > 0 && (
                <Badge className="ml-2 bg-red-500 text-white">{pendingItems.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved" className="rounded-lg px-6">
              <CheckCircle className="h-4 w-4 mr-2" />
              Approved
            </TabsTrigger>
            <TabsTrigger value="content" className="rounded-lg px-6">
              <FileText className="h-4 w-4 mr-2" />
              All Content
            </TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-lg px-6 relative">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
              {unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg px-6">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="create-post" className="rounded-lg px-6">
              <Plus className="h-4 w-4 mr-2" />
              Create Post
            </TabsTrigger>
          </TabsList>

          {/* Tab Panel Components */}
          <OverviewTab
            analytics={analytics}
            growthData={growthData}
            pendingItems={pendingItems}
            getItemIcon={getItemIcon}
            getItemTitle={getItemTitle}
            handleReview={handleReview}
            handleRefresh={handleRefresh}
          />

          <ReviewsTab
            pendingItems={pendingItems}
            handleReview={handleReview}
            handleSetPrice={handleSetPrice}
            handleEdit={handleEdit}
            getItemIcon={getItemIcon}
            getItemTitle={getItemTitle}
          />

          <ApprovedTab
            allContent={allContent}
            contentTypeFilter={contentTypeFilter}
            setContentTypeFilter={setContentTypeFilter}
            handleSetPrice={handleSetPrice}
            getItemIcon={getItemIcon}
            getItemTitle={getItemTitle}
          />

          <ContentTab
            filteredContent={filteredContent}
            contentSearch={contentSearch}
            setContentSearch={setContentSearch}
            contentTypeFilter={contentTypeFilter}
            setContentTypeFilter={setContentTypeFilter}
            contentStatusFilter={contentStatusFilter}
            setContentStatusFilter={setContentStatusFilter}
            contentSortBy={contentSortBy}
            setContentSortBy={setContentSortBy}
            dateRangeFilter={dateRangeFilter}
            setDateRangeFilter={setDateRangeFilter}
            priceRangeFilter={priceRangeFilter}
            setPriceRangeFilter={setPriceRangeFilter}
            unreadNotifications={unreadNotifications}
            fetchNotifications={fetchNotifications}
            handleReview={handleReview}
            handleSetPrice={handleSetPrice}
            handleEdit={handleEdit}
            getItemIcon={getItemIcon}
            getItemTitle={getItemTitle}
          />

          <NotificationsTab
            notifications={notifications}
            unreadNotifications={unreadNotifications}
            fetchNotifications={fetchNotifications}
          />

          <UsersTab
            filteredUsers={filteredUsers}
            paginatedUsers={paginatedUsers}
            userSearch={userSearch}
            setUserSearch={setUserSearch}
            userRoleFilter={userRoleFilter}
            setUserRoleFilter={setUserRoleFilter}
            userStatusFilter={userStatusFilter}
            setUserStatusFilter={setUserStatusFilter}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            handleAddCoins={handleAddCoins}
            handleSuspendUser={handleSuspendUser}
            handleDeleteUser={handleDeleteUser}
            handleToggleVerification={handleToggleVerification}
            handleEdit={handleEdit}
          />

          <CreatePostTab
            jobForm={jobForm}
            setJobForm={setJobForm}
            tuitionForm={tuitionForm}
            setTuitionForm={setTuitionForm}
            handleCreateJob={handleCreateJob}
            handleCreateTuition={handleCreateTuition}
            adminPostSearch={adminPostSearch}
            setAdminPostSearch={setAdminPostSearch}
            adminPostFilter={adminPostFilter}
            setAdminPostFilter={setAdminPostFilter}
            filteredAdminJobs={filteredAdminJobs}
            filteredAdminTuitions={filteredAdminTuitions}
            setEditingAdminPost={setEditingAdminPost}
          />
        </Tabs>
      </div>

      {/* Modal Components */}
      <ReviewModal
        reviewModal={reviewModal}
        setReviewModal={setReviewModal}
        approveItem={approveItem}
        rejectItem={rejectItem}
        getItemIcon={getItemIcon}
        getItemTitle={getItemTitle}
      />

      <PriceModal
        priceModal={priceModal}
        setPriceModal={setPriceModal}
        savePrice={savePrice}
        getItemTitle={getItemTitle}
      />

      <WalletModal
        walletModal={walletModal}
        setWalletModal={setWalletModal}
        addWalletCoins={addWalletCoins}
      />

      <DeleteModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        confirmDeleteUser={confirmDeleteUser}
      />

      <EditModal
        editModal={editModal}
        setEditModal={setEditModal}
        saveEdit={saveEdit}
      />

      <EditAdminPostModal
        editingAdminPost={editingAdminPost}
        setEditingAdminPost={setEditingAdminPost}
        handleEditAdminPost={handleEditAdminPost}
      />
    </div>
  );
};

export default AdminDashboard;
